import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard,
  FolderOpen,
  CheckSquare,
  Users,
  Calculator,
  FileText,
  TrendingUp,
  BarChart3,
  Calendar,
  Clock,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  Plus,
  GitBranch,
  Package,
  GanttChart,
  PanelLeftClose,
  Sun,
  Moon,
  Search,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import TaskModal from '../tasks/TaskModal';
import NotificationCenter from '../notifications/NotificationCenter';
import CommandPalette from './CommandPalette';
import { getUnreadNotifications, subscribeToNotifications } from '../../lib/notifications';

interface LayoutProps {
  children: React.ReactNode;
}

const navigationSections = [
  {
    title: 'OPERACIONES',
    items: [
      { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
      { name: 'Proyectos', href: '/projects', icon: FolderOpen },
      { name: 'Tareas', href: '/tasks', icon: CheckSquare },
      { name: 'Calendario', href: '/calendar', icon: Calendar },
      { name: 'Timeline', href: '/gantt', icon: GanttChart },
      { name: 'Material', href: '/equipment', icon: Package },
      { name: 'Tiempo', href: '/time-tracking', icon: Clock },
    ],
  },
  {
    title: 'NEGOCIO',
    items: [
      { name: 'Pipeline', href: '/pipeline', icon: GitBranch },
      { name: 'Clientes', href: '/clients', icon: Users },
      { name: 'Presupuestos', href: '/budgets', icon: Calculator },
      { name: 'Facturas', href: '/invoices', icon: FileText },
      { name: 'Rentabilidad', href: '/profitability', icon: TrendingUp },
    ],
  },
  {
    title: 'ANÁLISIS',
    items: [
      { name: 'Forecast', href: '/forecast', icon: BarChart3 },
    ],
  },
  {
    title: '',
    items: [
      { name: 'Configuración', href: '/settings', icon: Settings },
    ],
  },
];

const SIDEBAR_STORAGE_KEY = 'plannnr:sidebar-collapsed';

export default function Layout({ children }: LayoutProps) {
  const { profile, signOut } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [notificationCenterOpen, setNotificationCenterOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [theme, setTheme] = useState<'dark' | 'light'>(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('light')
      ? 'light'
      : 'dark'
  );

  const toggleTheme = () => {
    setTheme((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      document.documentElement.classList.toggle('light', next === 'light');
      try {
        localStorage.setItem('avpro:theme', next);
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [collapsed]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCmdOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    loadUnreadCount();
    const unsubscribe = subscribeToNotifications(() => {
      loadUnreadCount();
    });
    return () => {
      unsubscribe();
    };
  }, []);

  const loadUnreadCount = async () => {
    const unread = await getUnreadNotifications();
    setUnreadCount(unread.length);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const userInitial = (profile?.name || profile?.full_name)?.charAt(0) || 'U';

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="fixed top-0 left-0 bottom-0 w-72 max-w-[85vw] bg-gray-900 border-r border-gray-800 shadow-xl overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-800">
              <h1 className="text-xl font-bold text-gray-50 font-display">plannnr</h1>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-300 active:bg-gray-800 rounded-lg"
                aria-label="Cerrar menú"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="p-3">
              {navigationSections.map((section, sectionIdx) => (
                <div key={sectionIdx}>
                  {section.title && (
                    <div className="px-4 mt-6 mb-2 section-title">{section.title}</div>
                  )}
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <NavLink
                        key={item.name}
                        to={item.href}
                        onClick={() => setSidebarOpen(false)}
                        className={({ isActive }) =>
                          `flex items-center px-4 py-3.5 rounded-lg mb-1 transition-all duration-150 ${
                            isActive
                              ? 'bg-brand-600 text-white shadow-sm'
                              : 'text-gray-300 hover:bg-gray-800 active:bg-gray-700 hover:text-gray-50'
                          }`
                        }
                      >
                        <Icon className="w-6 h-6 mr-3" />
                        <span className="text-base">{item.name}</span>
                      </NavLink>
                    );
                  })}
                </div>
              ))}
            </nav>
            <div className="p-4 border-t border-gray-800">
              <button
                onClick={handleSignOut}
                className="flex items-center w-full px-4 py-3.5 text-gray-300 hover:bg-gray-800 active:bg-gray-700 hover:text-danger-400 rounded-lg transition-all duration-150"
              >
                <LogOut className="w-6 h-6 mr-3" />
                <span className="text-base">Cerrar sesión</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div
        className={`hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col lg:bg-gray-900 lg:border-r lg:border-gray-800 transition-[width] duration-200 ease-out ${
          collapsed ? 'lg:w-16' : 'lg:w-64'
        }`}
      >
        <div
          className={`flex items-center h-16 border-b border-gray-800 ${
            collapsed ? 'justify-center px-2' : 'justify-between px-4'
          }`}
        >
          {collapsed ? (
            <button
              onClick={() => setCollapsed(false)}
              className="w-9 h-9 bg-gradient-to-br from-brand-500 to-accent-600 rounded-lg flex items-center justify-center hover:opacity-90 transition-opacity"
              title="Expandir menú"
              aria-label="Expandir menú"
            >
              <span className="text-sm font-bold text-gray-50">P</span>
            </button>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-brand-500 to-accent-600 rounded-lg flex items-center justify-center">
                  <span className="text-sm font-bold text-gray-50">P</span>
                </div>
                <h1 className="text-xl font-bold text-gray-50 font-display">plannnr</h1>
              </div>
              <button
                onClick={() => setCollapsed(true)}
                className="p-1.5 text-gray-500 hover:text-gray-300 hover:bg-gray-800 rounded-lg transition-colors"
                title="Colapsar menú"
                aria-label="Colapsar menú"
              >
                <PanelLeftClose className="w-5 h-5" />
              </button>
            </>
          )}
        </div>

        <nav className="flex-1 p-2 overflow-y-auto overflow-x-hidden">
          {navigationSections.map((section, sectionIdx) => (
            <div key={sectionIdx}>
              {section.title && !collapsed && (
                <div className="px-3 mt-6 mb-2 section-title">{section.title}</div>
              )}
              {section.title && collapsed && sectionIdx > 0 && (
                <div className="mx-3 my-3 border-t border-gray-800" />
              )}
              {section.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={({ isActive }) =>
                      `flex items-center px-3 py-2.5 rounded-lg mb-1 transition-colors duration-150 ${
                        collapsed ? 'justify-center' : ''
                      } ${
                        isActive
                          ? 'bg-brand-600 text-white shadow-sm'
                          : 'text-gray-400 hover:bg-gray-800 hover:text-gray-50'
                      }`
                    }
                    title={collapsed ? item.name : undefined}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {!collapsed && (
                      <span className="ml-3 whitespace-nowrap text-sm">{item.name}</span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="p-3 border-t border-gray-800">
          {collapsed ? (
            <div className="flex flex-col items-center gap-2">
              <div
                className="w-9 h-9 bg-gradient-to-br from-gray-700 to-gray-800 rounded-full flex items-center justify-center shadow-lg"
                title={profile?.name || profile?.full_name || 'Usuario'}
              >
                <span className="text-sm font-semibold text-gray-50">{userInitial}</span>
              </div>
              <button
                onClick={handleSignOut}
                className="p-2 text-gray-400 hover:text-danger-400 transition-colors"
                title="Cerrar sesión"
                aria-label="Cerrar sesión"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center min-w-0">
                <div className="w-9 h-9 bg-gradient-to-br from-gray-700 to-gray-800 rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
                  <span className="text-sm font-semibold text-gray-50">{userInitial}</span>
                </div>
                <div className="ml-3 min-w-0">
                  <p className="text-sm font-medium text-gray-200 truncate">
                    {profile?.name || profile?.full_name}
                  </p>
                  <p className="text-xs text-gray-500 capitalize">{profile?.role}</p>
                </div>
              </div>
              <button
                onClick={handleSignOut}
                className="p-2 text-gray-400 hover:text-danger-400 transition-colors flex-shrink-0"
                title="Cerrar sesión"
                aria-label="Cerrar sesión"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      <div
        className={`transition-[padding] duration-200 ease-out ${
          collapsed ? 'lg:pl-16' : 'lg:pl-64'
        }`}
      >
        {/* Top bar */}
        <div className="sticky top-0 z-40 bg-gray-900/90 backdrop-blur border-b border-gray-800">
          <div className="flex items-center justify-between px-3 sm:px-4 md:px-6 py-3">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2.5 text-gray-400 hover:text-gray-300 active:bg-gray-800 rounded-lg lg:hidden"
                aria-label="Abrir menú"
              >
                <Menu className="w-6 h-6" />
              </button>
              <h2 className="text-lg font-semibold text-gray-50 font-display lg:hidden">plannnr</h2>
              <button
                onClick={() => setCmdOpen(true)}
                className="hidden lg:flex items-center gap-2 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-400 hover:text-gray-200 hover:border-gray-600 transition-colors"
                title="Buscar (⌘K)"
              >
                <Search className="w-4 h-4" />
                <span>Buscar…</span>
                <span className="ml-3 text-[10px] font-mono bg-gray-900 border border-gray-700 rounded px-1.5 py-0.5">⌘K</span>
              </button>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => setIsTaskModalOpen(true)}
                className="btn-primary flex items-center gap-2"
                title="Crear Tarea"
              >
                <Plus className="w-5 h-5" />
                <span className="hidden sm:inline">Crear Tarea</span>
              </button>
              <button
                onClick={toggleTheme}
                className="p-2.5 text-gray-400 hover:text-gray-200 active:bg-gray-800 rounded-lg transition-colors"
                title={theme === 'dark' ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'}
                aria-label="Cambiar tema"
              >
                {theme === 'dark' ? <Sun className="w-6 h-6" /> : <Moon className="w-6 h-6" />}
              </button>
              <div className="relative">
                <button
                  onClick={() => setNotificationCenterOpen(!notificationCenterOpen)}
                  className="p-2.5 text-gray-400 hover:text-gray-300 active:bg-gray-800 rounded-lg relative transition-colors"
                  title="Notificaciones"
                  aria-label="Notificaciones"
                >
                  <Bell className="w-6 h-6" />
                  {unreadCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 px-1 min-w-[20px] h-[20px] bg-danger-500 text-white text-xs font-bold rounded-full flex items-center justify-center ring-2 ring-gray-900">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
                <NotificationCenter
                  isOpen={notificationCenterOpen}
                  onClose={() => {
                    setNotificationCenterOpen(false);
                    loadUnreadCount();
                  }}
                />
              </div>
              <div className="flex items-center lg:hidden">
                <div className="w-9 h-9 bg-gradient-to-br from-gray-700 to-gray-800 rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-sm font-semibold text-gray-50">{userInitial}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="p-3 sm:p-4 md:p-6 pb-24 lg:pb-6">{children}</main>
      </div>

      {/* Bottom nav (móvil) */}
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-gray-900/95 backdrop-blur border-t border-gray-800 flex items-stretch"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {[
          { name: 'Inicio', href: '/dashboard', icon: LayoutDashboard },
          { name: 'Tareas', href: '/tasks', icon: CheckSquare },
          { name: 'Agenda', href: '/calendar', icon: Calendar },
        ].map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.name}
              to={item.href}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 text-[11px] transition-colors ${
                  isActive ? 'text-brand-400' : 'text-gray-400'
                }`
              }
            >
              <Icon className="w-5 h-5" />
              <span>{item.name}</span>
            </NavLink>
          );
        })}
        <button
          onClick={() => setCmdOpen(true)}
          className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 text-[11px] text-gray-400"
          aria-label="Buscar"
        >
          <Search className="w-5 h-5" />
          <span>Buscar</span>
        </button>
        <button
          onClick={() => setSidebarOpen(true)}
          className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 text-[11px] text-gray-400"
          aria-label="Menú"
        >
          <Menu className="w-5 h-5" />
          <span>Menú</span>
        </button>
      </nav>

      <TaskModal
        isOpen={isTaskModalOpen}
        onClose={() => setIsTaskModalOpen(false)}
        task={null}
        projectId=""
        projectName=""
        onSave={() => setIsTaskModalOpen(false)}
      />

      <CommandPalette
        isOpen={cmdOpen}
        onClose={() => setCmdOpen(false)}
        onCreateTask={() => {
          setCmdOpen(false);
          setIsTaskModalOpen(true);
        }}
      />
    </div>
  );
}
